import { formatUnknownError } from "@project/shared";
import {
  type Group,
  LoadingManager,
  type Material,
  type Mesh,
  type Object3DEventMap,
  type Texture,
} from "three";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { restrictToAssetOrigin } from "./assetUrlGuard";

/** Texture slots MTLLoader fills on its Phong materials. */
const TEXTURE_SLOTS = [
  "map",
  "specularMap",
  "emissiveMap",
  "normalMap",
  "bumpMap",
  "displacementMap",
  "alphaMap",
] as const;

const toError = (err: unknown): Error =>
  err instanceof Error ? err : new Error(formatUnknownError(err));

/**
 * Removes the textures whose image never arrived. MTLLoader assigns a texture
 * before its image loads; one that failed would sample as black.
 */
function dropFailedTextures(group: Group): void {
  group.traverse((child) => {
    const mesh = child as Mesh;
    if (!mesh.isMesh) return;
    const materials: Material[] = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    for (const material of materials) {
      const slots = material as unknown as Record<string, Texture | null>;
      for (const slot of TEXTURE_SLOTS) {
        const texture = slots[slot];
        if (
          texture &&
          (texture.image === null || texture.image === undefined)
        ) {
          slots[slot] = null;
          material.needsUpdate = true;
        }
      }
    }
  });
}

/**
 * Loads an OBJ with the materials of a separate MTL. Textures resolve relative
 * to the MTL's directory. A texture that fails to load leaves its material
 * untextured; only a failing OBJ or MTL rejects.
 *
 * Resolves once the OBJ is parsed and no request of this load is pending. The
 * pending requests are counted here rather than taken from the manager's
 * onLoad: three's FileLoader shares a concurrent request for the same URL
 * without registering it with the second manager, so that manager may finish
 * before the OBJ, or never start at all.
 */
export function loadObjWithMaterial(
  objUrl: string,
  mtlUrl: string,
): Promise<Group<Object3DEventMap>> {
  return new Promise((resolve, reject) => {
    let loadedGroup: Group<Object3DEventMap> | null = null;
    let pendingRequests = 0;
    const resolveWhenComplete = () => {
      if (!loadedGroup || pendingRequests > 0) return;
      dropFailedTextures(loadedGroup);
      resolve(loadedGroup);
    };

    const manager = new LoadingManager(undefined, undefined, (url) =>
      console.warn(`Failed to load mesh asset: ${url}`),
    );
    const { itemStart, itemEnd } = manager;
    manager.itemStart = (url) => {
      pendingRequests++;
      itemStart(url);
    };
    manager.itemEnd = (url) => {
      pendingRequests--;
      itemEnd(url);
      resolveWhenComplete();
    };
    restrictToAssetOrigin(manager, mtlUrl);

    const mtlLoader = new MTLLoader(manager);
    const slash = mtlUrl.lastIndexOf("/");
    if (slash >= 0) {
      mtlLoader.setResourcePath(mtlUrl.slice(0, slash + 1));
    }
    const objLoader = new OBJLoader(manager);
    mtlLoader.load(
      mtlUrl,
      (materials) => {
        materials.preload();
        objLoader.setMaterials(materials);
        objLoader.load(
          objUrl,
          (group) => {
            loadedGroup = group;
            resolveWhenComplete();
          },
          undefined,
          (err) => reject(toError(err)),
        );
      },
      undefined,
      (err) => reject(toError(err)),
    );
  });
}
